import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Users, DollarSign, Calendar, TrendingUp, FileText,
  CheckCircle, XCircle, AlertCircle, ArrowUpRight, Briefcase, CalendarClock,
  MailPlus, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import TodayRunSheet from '../../components/TodayRunSheet';
import { PendingAllergyFormsWidget } from '../../components/PendingAllergyFormsWidget';
import useMessageStream from '../../hooks/useMessageStream';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Clickable, brand-coloured stat card that navigates on click.
const StatCard = ({ icon: Icon, label, value, color, delay, onClick, testId, sub, clickable = true }) => (
  <motion.button
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={clickable ? { y: -4, scale: 1.02 } : {}}
    whileTap={clickable ? { scale: 0.99 } : {}}
    className={`text-left w-full bg-white rounded-2xl border-2 p-6 transition-[box-shadow,border-color] duration-300 ${clickable ? 'cursor-pointer focus:outline-none focus-visible:ring-4' : 'cursor-default'}`}
    style={{ borderColor: `${color}22` }}
    onMouseEnter={(e) => {
      if (!clickable) return;
      e.currentTarget.style.boxShadow = `0 16px 32px -16px ${color}60, 0 0 0 2px ${color}`;
      e.currentTarget.style.borderColor = color;
    }}
    onMouseLeave={(e) => {
      if (!clickable) return;
      e.currentTarget.style.boxShadow = '';
      e.currentTarget.style.borderColor = `${color}22`;
    }}
    data-testid={testId}
    aria-label={`${label} ${value}${onClick ? ' — open details' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{value}</p>
          <p className="text-sm text-[#6B6B7A]">{label}</p>
          {sub && <p className="text-[11px] text-[#9a9aa6] mt-0.5">{sub}</p>}
        </div>
      </div>
      {clickable && (
        <ArrowUpRight className="w-4 h-4 text-[#9a9aa6] transition-transform duration-200 group-hover:translate-x-0.5" />
      )}
    </div>
  </motion.button>
);

export const CrmOverview = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);
  // Real-time inbox cue (blinking pill on the Dashboard)
  const [newEmail, setNewEmail] = useState(null); // { count, mailboxes, at } or null
  const [refreshingInbox, setRefreshingInbox] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, revRes, custRes, intRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/analytics/revenue`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/analytics/customers`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/integration-status`, { withCredentials: true })
            .catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setRevenueData(revRes.data);
        setCustomerData(custRes.data);
        setIntegrations(intRes.data);
      } catch (e) {
        toast.error('Failed to load CRM overview');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Listen to SSE for `new_email` events and surface a blinking pill.
  useMessageStream(useCallback((evt) => {
    if (evt?.type !== 'new_email') return;
    setNewEmail({
      count: Number(evt.new_count) || 0,
      mailboxes: evt.new_by_mailbox || {},
      at: evt.at,
    });
  }, []));

  const handleRefreshInbox = useCallback(async () => {
    setRefreshingInbox(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/emails/refresh`, {}, { withCredentials: true }
      );
      const stored = (res.data?.results || []).reduce((s, r) => s + (r.stored || 0), 0);
      if (stored > 0) {
        toast.success(`Pulled ${stored} new ${stored === 1 ? 'email' : 'emails'} from IONOS`);
      } else {
        toast.info('Inbox is up to date');
      }
      // The backend will fan out a `new_email` event if anything new landed.
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not refresh inbox');
    } finally {
      setRefreshingInbox(false);
    }
  }, []);

  const handleOpenInbox = useCallback(() => {
    setNewEmail(null);
    navigate('/crm/emails');
  }, [navigate]);

  const handleDismissNewEmail = useCallback(() => setNewEmail(null), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" />
      </div>
    );
  }

  // Navigation helpers
  const goAdminWith = (params) => {
    const qs = new URLSearchParams(params).toString();
    navigate(`/admin${qs ? `?${qs}` : ''}`);
  };
  const monthStart = stats?.month_start;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            CRM Overview
          </h1>
          <p className="text-[#6B6B7A]">Click any card to drill into its filtered list.</p>
        </div>
        <div className="flex items-center gap-3">
          <AnimatePresence>
            {newEmail && newEmail.count > 0 && (
              <motion.div
                key="new-email-pill"
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 360, damping: 26 }}
                className="flex items-center gap-1"
                data-testid="dashboard-new-email-pill"
              >
                <button
                  type="button"
                  onClick={handleOpenInbox}
                  className="cb-blink-pill relative inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-semibold shadow-lg"
                  style={{ background: 'linear-gradient(135deg,#FF3B6B 0%,#FF7A2E 100%)' }}
                  data-testid="dashboard-new-email-btn"
                  aria-label={`${newEmail.count} new ${newEmail.count === 1 ? 'email' : 'emails'} received`}
                >
                  <span className="cb-pulse-dot" />
                  <MailPlus className="w-4 h-4" />
                  {newEmail.count} new {newEmail.count === 1 ? 'email' : 'emails'}
                </button>
                <button
                  type="button"
                  onClick={handleRefreshInbox}
                  disabled={refreshingInbox}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:text-[#1E1E24] hover:bg-[#FFFDF8] transition disabled:opacity-50"
                  title="Refresh inbox"
                  data-testid="dashboard-new-email-refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshingInbox ? 'animate-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={handleDismissNewEmail}
                  className="text-[11px] font-semibold text-[#6B6B7A] hover:text-[#1E1E24] px-2"
                  data-testid="dashboard-new-email-dismiss"
                  aria-label="Dismiss new email notice"
                >
                  Dismiss
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF3B6B] hover:text-[#E62E58]"
            data-testid="overview-open-bookings-list-btn"
          >
            View bookings list <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Today's Run Sheet */}
      <TodayRunSheet />

      {/* Party-day safety — pending allergy/consent forms (hides itself when empty) */}
      <PendingAllergyFormsWidget />

      {/* Row 1: Core metrics (clickable) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Calendar} label="Total Bookings" value={stats?.total_bookings || 0}
          color="#FFD166" delay={0.0}
          sub="All-time"
          onClick={() => goAdminWith({})}
          testId="card-total-bookings"
        />
        <StatCard
          icon={CalendarClock} label="Upcoming Parties" value={stats?.upcoming_bookings || 0}
          color="#06D6A0" delay={0.05}
          sub="Next 7 days"
          onClick={() => goAdminWith({ view: 'byDate', date_from: today })}
          testId="card-upcoming-parties"
        />
        <StatCard
          icon={AlertCircle} label="Pending Payments" value={stats?.pending_payments || 0}
          color="#FF7A2E" delay={0.1}
          sub="Deposit paid · balance due"
          onClick={() => goAdminWith({ balance_due: 'true', date_from: today })}
          testId="card-pending-payments"
        />
        <StatCard
          icon={DollarSign} label="Outstanding Balance"
          value={`$${(stats?.outstanding_balance || 0).toLocaleString('en-CA', { minimumFractionDigits: 2 })}`}
          color="#FF3B6B" delay={0.15}
          sub={`${stats?.outstanding_count || 0} bookings`}
          onClick={() => goAdminWith({ outstanding: 'true' })}
          testId="card-outstanding-balance"
        />
      </div>

      {/* Row 2: Secondary metrics (clickable) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={DollarSign} label="Net Revenue"
          value={`$${(revenueData?.net_revenue || 0).toLocaleString('en-CA', { minimumFractionDigits: 0 })}`}
          color="#06D6A0" delay={0.2}
          sub="All completed"
          onClick={() => navigate('/crm/payments')}
          testId="card-net-revenue"
        />
        <StatCard
          icon={Users} label="Total Customers" value={customerData?.total_customers || 0}
          color="#6366F1" delay={0.25}
          sub={`${customerData?.retention_rate || 0}% retention`}
          onClick={() => navigate('/crm/customers')}
          testId="card-total-customers"
        />
        <StatCard
          icon={XCircle} label="Cancelled This Month" value={stats?.cancelled_this_month || 0}
          color="#EF4444" delay={0.3}
          sub={monthStart ? `Since ${monthStart}` : ''}
          onClick={() => goAdminWith({ status: 'cancelled', date_from: monthStart || '' })}
          testId="card-cancelled-this-month"
        />
        <StatCard
          icon={Briefcase} label="Active Employees" value={stats?.active_employees || 0}
          color="#1E1E24" delay={0.35}
          sub="On the roster"
          onClick={() => navigate('/crm/employees?active=true')}
          testId="card-active-employees"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6">
          <h3 className="text-lg font-semibold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Revenue by Package
          </h3>
          {revenueData?.by_package?.length > 0 ? (
            <div className="space-y-4">
              {revenueData.by_package.map((pkg, i) => {
                const colors = ['#FF3B6B', '#06D6A0', '#FFD166'];
                const maxRevenue = Math.max(...revenueData.by_package.map(p => p.revenue));
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#1E1E24] font-medium">{pkg.package}</span>
                      <span className="text-[#6B6B7A]">${pkg.revenue.toLocaleString()} ({pkg.count} bookings)</span>
                    </div>
                    <div className="w-full bg-[#F5F3EF] rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(pkg.revenue / maxRevenue) * 100}%` }}
                        transition={{ duration: 0.8, delay: i * 0.1 }}
                        className="h-3 rounded-full"
                        style={{ backgroundColor: colors[i % colors.length] }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[#6B6B7A] text-center py-8">No revenue data yet</p>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6">
          <h3 className="text-lg font-semibold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Monthly Revenue Trend
          </h3>
          {revenueData?.monthly_revenue?.length > 0 ? (
            <div className="space-y-3">
              {revenueData.monthly_revenue.slice(-6).map((m, i) => {
                const maxRev = Math.max(...revenueData.monthly_revenue.map(r => r.revenue));
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#6B6B7A]">{m.month}</span>
                      <span className="text-[#1E1E24] font-medium">${m.revenue.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-[#F5F3EF] rounded-full h-2">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(m.revenue / (maxRev || 1)) * 100}%` }}
                        transition={{ duration: 0.6, delay: i * 0.08 }}
                        className="h-2 rounded-full bg-[#06D6A0]"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[#6B6B7A] text-center py-8">No monthly data yet</p>
          )}
        </div>
      </div>

      {/* Integration Status */}
      {integrations && (
        <div className="mt-6 bg-white rounded-2xl border border-[#E8E5DF] p-6">
          <h3 className="text-lg font-semibold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Integration Status
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(integrations).map(([key, val]) => (
              <div key={key} className={`flex items-center gap-3 p-4 rounded-xl border ${val.configured ? 'border-[#06D6A0]/30 bg-[#06D6A0]/5' : 'border-[#E8E5DF] bg-[#FFFDF8]'}`}>
                {val.configured ? <CheckCircle className="w-5 h-5 text-[#06D6A0]" /> : <XCircle className="w-5 h-5 text-[#6B6B7A]" />}
                <div>
                  <p className="text-sm font-medium text-[#1E1E24]">{val.label}</p>
                  <p className={`text-xs ${val.configured ? 'text-[#06D6A0]' : 'text-[#6B6B7A]'}`}>
                    {val.configured ? 'Connected' : 'Not configured (mocked)'}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {Object.values(integrations).some(v => !v.configured) && (
            <p className="text-xs text-[#6B6B7A] mt-3">
              Add API keys in backend .env file to enable live integrations. Messages will be mocked until configured.
            </p>
          )}
        </div>
      )}

      {/* Hidden icons kept for import stability */}
      <span className="hidden"><BarChart3 /><TrendingUp /><FileText /></span>
    </div>
  );
};

export default CrmOverview;
